// SME Capture Flow — client hook for the Phase 1 capture loop.
//
// This is the smallest slice (docs/sme-capture-implementation-plan.md §8 M2):
// create a single-track capture session, narrate freely, and watch the draft
// events the server silently extracts. Later slices (order, problems,
// attribution, stages, multi-track) extend the model surfaced here.

import { useState, useCallback, useEffect } from 'react';
import { TimelineEntry } from '../utils/captureTimeline';
import { StageEntry } from '../utils/captureStages';

export interface CaptureEvent {
  id: string;
  text: string;
  fromDocument?: boolean;
  draft?: boolean;
}

export type ProblemKind = 'broken' | 'uncertain' | 'disputed' | 'depends';
export type ProblemSource = 'flagged' | 'dont-know' | 'cross-track';

export interface CaptureProblem {
  id: string;
  anchorEventId: string | null;
  kind: ProblemKind;
  text: string;
  source: ProblemSource;
  resolved: boolean;
  resolution: string;
}

export type TriggerKind = 'person' | 'automatic';

export interface Attribution {
  trigger: { kind: TriggerKind; who: string };
  systems: string[];
}

// One parallel expert track (spec Phase 1: each expert describes the story on
// their own separate track; reconciliation merges them in Phase 2).
export interface CaptureTrack {
  trackId: string;
  expertLabel: string;
  rawEvents: CaptureEvent[];
}

// Every action resolves to a success boolean — failures are also surfaced via
// `error`, but callers must NOT treat a resolved promise as success (the shell
// previously advanced views and showed "Saved" after failed requests).
export interface UseCapture {
  // The ACTIVE track (the expert currently describing); `tracks` lists all.
  trackId: string | null;
  tracks: CaptureTrack[];
  switchTrack: (trackId: string) => void;
  addExpert: (label: string) => Promise<boolean>;
  scope: { start: string; end: string };
  saveScope: (start: string, end: string) => Promise<boolean>;
  seedFromText: (text: string) => Promise<boolean>;
  events: CaptureEvent[];
  timeline: TimelineEntry[];
  problems: CaptureProblem[];
  attributions: Record<string, Attribution>;
  stages: StageEntry[];
  nudge: string;
  busy: boolean;
  error: string;
  loading: boolean;
  start: () => Promise<string>;
  describe: (message: string) => Promise<boolean>;
  order: () => Promise<boolean>;
  saveTimeline: (entries: TimelineEntry[]) => Promise<boolean>;
  flagProblem: (text: string, kind?: ProblemKind, anchorEventId?: string | null) => Promise<boolean>;
  resolveProblem: (id: string, resolution?: string) => Promise<boolean>;
  setAttribution: (eventId: string, who: string, systems?: string[]) => Promise<boolean>;
  deriveHidden: () => Promise<boolean>;
  proposeStages: () => Promise<boolean>;
  saveStages: (stages: StageEntry[]) => Promise<boolean>;
  // Convert this capture into a discovery process map; resolves the new
  // discovery sessionId (to navigate to /sessions/:id), or null on failure.
  toDiscovery: () => Promise<string | null>;
}

export function useCapture(sessionId?: string): UseCapture {
  const [tracks, setTracks] = useState<CaptureTrack[]>([]);
  const [trackId, setTrackId] = useState<string | null>(null);
  const [scope, setScope] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [problems, setProblems] = useState<CaptureProblem[]>([]);
  const [attributions, setAttributions] = useState<Record<string, Attribution>>({});
  const [stages, setStages] = useState<StageEntry[]>([]);
  const [nudge, setNudge] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!!sessionId);

  // Load an existing capture session: hydrate the single track + its events.
  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/capture/sessions/${sessionId}`);
        if (!res.ok) throw new Error('Failed to load capture session');
        const model = await res.json();
        if (cancelled) return;
        const loaded: CaptureTrack[] = (model.tracks ?? []).map(
          (t: { trackId: string; expertLabel?: string; rawEvents?: CaptureEvent[] }) => ({
            trackId: t.trackId,
            expertLabel: t.expertLabel ?? '',
            rawEvents: t.rawEvents ?? [],
          }),
        );
        setTracks(loaded);
        setTrackId(loaded[0]?.trackId ?? null);
        setScope({ start: model.scope?.start ?? '', end: model.scope?.end ?? '' });
        setTimeline(model.timeline ?? []);
        setProblems(model.problems ?? []);
        setAttributions(model.attributions ?? {});
        setStages(model.stages ?? []);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Create a new capture session with one expert track. Returns the new id so
  // the caller can navigate to /capture/:id.
  const start = useCallback(async (): Promise<string> => {
    const res = await fetch('/api/capture/sessions', { method: 'POST' });
    if (!res.ok) throw new Error('Failed to create capture session');
    const model = await res.json();
    const id = model.id as string;
    const tRes = await fetch(`/api/capture/sessions/${id}/tracks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expertLabel: '' }),
    });
    if (!tRes.ok) throw new Error('Failed to start track');
    return id;
  }, []);

  // One Phase 1 turn: send the narrative, append the silently-extracted draft
  // events, and surface any "keep going" nudge.
  const describe = useCallback(
    async (message: string): Promise<boolean> => {
      if (!sessionId || !trackId || !message.trim()) return false;
      setBusy(true);
      setError('');
      setNudge('');
      try {
        const res = await fetch(`/api/capture/sessions/${sessionId}/tracks/${trackId}/describe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message }),
        });
        if (!res.ok) throw new Error('Capture failed');
        const data = await res.json();
        if (Array.isArray(data.events) && data.events.length) {
          setTracks((prev) =>
            prev.map((t) =>
              t.trackId === trackId ? { ...t, rawEvents: [...t.rawEvents, ...data.events] } : t,
            ),
          );
        }
        // "I don't know / it depends" is captured server-side as a problem.
        if (data.problem) setProblems((prev) => [...prev, data.problem]);
        if (data.nudge) setNudge(data.nudge);
        return true;
      } catch (e) {
        setError((e as Error).message);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [sessionId, trackId],
  );

  // Switch which expert's track is active (Phase 1 — parallel capture).
  const switchTrack = useCallback((id: string) => {
    setTrackId(id);
    setNudge('');
  }, []);

  // Add another expert's track and make it active. Each expert describes on
  // their own track; ordering reconciles them and flags disagreements.
  const addExpert = useCallback(
    async (label: string): Promise<boolean> => {
      if (!sessionId) return false;
      setError('');
      try {
        const res = await fetch(`/api/capture/sessions/${sessionId}/tracks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ expertLabel: label }),
        });
        if (!res.ok) throw new Error('Could not add the expert');
        const data = await res.json();
        const loaded: CaptureTrack[] = (data.model?.tracks ?? []).map(
          (t: { trackId: string; expertLabel?: string; rawEvents?: CaptureEvent[] }) => ({
            trackId: t.trackId,
            expertLabel: t.expertLabel ?? '',
            rawEvents: t.rawEvents ?? [],
          }),
        );
        setTracks(loaded);
        if (data.trackId) setTrackId(data.trackId);
        setNudge('');
        return true;
      } catch (e) {
        setError((e as Error).message);
        return false;
      }
    },
    [sessionId],
  );

  // Phase 0 — frame: record where the process starts and ends.
  const saveScope = useCallback(
    async (start: string, end: string): Promise<boolean> => {
      if (!sessionId) return false;
      setError('');
      try {
        const res = await fetch(`/api/capture/sessions/${sessionId}/scope`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ start, end }),
        });
        if (!res.ok) throw new Error('Could not save the scope');
        const data = await res.json();
        setScope({ start: data.scope?.start ?? start, end: data.scope?.end ?? end });
        return true;
      } catch (e) {
        setError((e as Error).message);
        return false;
      }
    },
    [sessionId],
  );

  // Phase 0 — seed: pull a DRAFT event list out of pasted document text onto
  // the active track. Documents describe the official process — every seeded
  // event arrives marked as a draft to confirm, never as fact.
  const seedFromText = useCallback(
    async (text: string): Promise<boolean> => {
      if (!sessionId || !trackId || !text.trim()) return false;
      setBusy(true);
      setError('');
      try {
        const res = await fetch(`/api/capture/sessions/${sessionId}/tracks/${trackId}/seed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        });
        if (!res.ok) throw new Error('Could not read the document');
        const data = await res.json();
        if (Array.isArray(data.events) && data.events.length) {
          setTracks((prev) =>
            prev.map((t) =>
              t.trackId === trackId ? { ...t, rawEvents: [...t.rawEvents, ...data.events] } : t,
            ),
          );
        }
        return true;
      } catch (e) {
        setError((e as Error).message);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [sessionId, trackId],
  );

  // Phase 2 — order: ask the server to merge + tidy + time-order the captured
  // events into a timeline proposal the expert can then review.
  const order = useCallback(async (): Promise<boolean> => {
    if (!sessionId) return false;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/api/capture/sessions/${sessionId}/order`, { method: 'POST' });
      if (!res.ok) throw new Error('Could not organise the timeline');
      const data = await res.json();
      setTimeline(data.timeline ?? []);
      // Ordering may raise cross-track disagreements as problems.
      if (data.model?.problems) setProblems(data.model.problems);
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [sessionId]);

  // Phase 2 — confirm: persist the expert's reordered/renamed timeline.
  const saveTimeline = useCallback(
    async (entries: TimelineEntry[]): Promise<boolean> => {
      if (!sessionId) return false;
      setBusy(true);
      setError('');
      try {
        const res = await fetch(`/api/capture/sessions/${sessionId}/timeline`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ timeline: entries }),
        });
        if (!res.ok) throw new Error('Could not save the timeline');
        const data = await res.json();
        setTimeline(data.timeline ?? entries);
        return true;
      } catch (e) {
        setError((e as Error).message);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [sessionId],
  );

  // Phase 3 — flag a problem (available from any phase). `anchorEventId` ties it
  // to a timeline point when known.
  const flagProblem = useCallback(
    async (text: string, kind: ProblemKind = 'uncertain', anchorEventId: string | null = null): Promise<boolean> => {
      if (!sessionId || !text.trim()) return false;
      setError('');
      try {
        const res = await fetch(`/api/capture/sessions/${sessionId}/problems`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, kind, anchorEventId }),
        });
        if (!res.ok) throw new Error('Could not flag the problem');
        const data = await res.json();
        if (data.model?.problems) setProblems(data.model.problems);
        return true;
      } catch (e) {
        setError((e as Error).message);
        return false;
      }
    },
    [sessionId],
  );

  // Phase 3 — resolve a problem (the facilitator addressed it). Problems are
  // never deleted, only marked resolved.
  const resolveProblem = useCallback(
    async (id: string, resolution = ''): Promise<boolean> => {
      if (!sessionId) return false;
      setError('');
      try {
        const res = await fetch(`/api/capture/sessions/${sessionId}/problems/${id}/resolve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resolution }),
        });
        if (!res.ok) throw new Error('Could not resolve the problem');
        const data = await res.json();
        if (data.model?.problems) setProblems(data.model.problems);
        return true;
      } catch (e) {
        setError((e as Error).message);
        return false;
      }
    },
    [sessionId],
  );

  // Phase 4 — attribution: the expert says who/what triggers an event; the
  // server infers person vs automatic and notes outside systems.
  const setAttribution = useCallback(
    async (eventId: string, who: string, systems: string[] = []): Promise<boolean> => {
      if (!sessionId) return false;
      setError('');
      try {
        const res = await fetch(`/api/capture/sessions/${sessionId}/attributions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eventId, who, systems }),
        });
        if (!res.ok) throw new Error('Could not save who triggers this');
        const data = await res.json();
        if (data.model?.attributions) setAttributions(data.model.attributions);
        return true;
      } catch (e) {
        setError((e as Error).message);
        return false;
      }
    },
    [sessionId],
  );

  // §5 — derive the hidden action→event layer from the timeline + attributions.
  // Built behind the scenes; not surfaced to the expert this pass.
  const deriveHidden = useCallback(async (): Promise<boolean> => {
    if (!sessionId) return false;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/api/capture/sessions/${sessionId}/derive-hidden`, { method: 'POST' });
      if (!res.ok) throw new Error('Could not build the model');
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [sessionId]);

  // Phase 5 — propose the natural divisions of the timeline (the system
  // suggests, the expert confirms — never a blank prompt).
  const proposeStages = useCallback(async (): Promise<boolean> => {
    if (!sessionId) return false;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/api/capture/sessions/${sessionId}/stages`, { method: 'POST' });
      if (!res.ok) throw new Error('Could not suggest stages');
      const data = await res.json();
      setStages(data.stages ?? []);
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [sessionId]);

  // Phase 5 — persist the expert's confirmed/adjusted stages.
  const saveStages = useCallback(
    async (entries: StageEntry[]): Promise<boolean> => {
      if (!sessionId) return false;
      setBusy(true);
      setError('');
      try {
        const res = await fetch(`/api/capture/sessions/${sessionId}/stages`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stages: entries }),
        });
        if (!res.ok) throw new Error('Could not save the stages');
        const data = await res.json();
        setStages(data.stages ?? entries);
        return true;
      } catch (e) {
        setError((e as Error).message);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [sessionId],
  );

  // The describe view shows the ACTIVE expert's events.
  const events = tracks.find((t) => t.trackId === trackId)?.rawEvents ?? [];

  // Translate the capture into a discovery process map and return the new id.
  const toDiscovery = useCallback(async (): Promise<string | null> => {
    if (!sessionId) return null;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/api/capture/sessions/${sessionId}/to-discovery`, { method: 'POST' });
      if (!res.ok) throw new Error('Could not turn this into a process map');
      const data = await res.json();
      return typeof data.sessionId === 'string' ? data.sessionId : null;
    } catch (e) {
      setError((e as Error).message);
      return null;
    } finally {
      setBusy(false);
    }
  }, [sessionId]);

  return {
    trackId,
    tracks,
    switchTrack,
    addExpert,
    scope,
    saveScope,
    seedFromText,
    events,
    timeline,
    problems,
    attributions,
    stages,
    nudge,
    busy,
    error,
    loading,
    start,
    describe,
    order,
    saveTimeline,
    flagProblem,
    resolveProblem,
    setAttribution,
    deriveHidden,
    proposeStages,
    saveStages,
    toDiscovery,
  };
}
